import { lireConversationsClients } from './messagerieClients'
import { supabase, appelerFonction } from './supabase'
import { MESSAGE_COMPTE_EXISTANT, adresseDejaInscrite, traduireErreurInscription } from './compte'
import { lireEcrituresManuelles, pieceManuelle } from './compta'
import { colonneReglementAbsente, moyenValide, traduireErreurReglement } from './reglement'
import { traduireErreurEncaissement } from './encaissement'
import { lireRapport, traduireErreurFacturation, type RapportFacturation } from './facturation'
import { diffCreneaux, traduireErreurAbonnement } from './abonnement'
import { LIBELLE_ROLE } from './roles'
import { afficherTelephone, contactAffiche, messageInvitation, type ContactValide, type ReponseInvitation } from './commun'
import { corpsCompteLivreur, corpsInvitation, type AccesCree } from './identifiants'
import type { Destinataire } from './destinataire'
import { cleTelephone, type AdresseConnue } from './adresses'
import { GRILLE_PAR_DEFAUT, OPTION_LONGUE_DISTANCE_MENSUEL_DEFAUT, type CompteTension, type TypeVehicule } from './prixOpere'
import { lireBoitiers, lireSourcesPosition, lireVehicules } from './boitiers'
import {
  STATUT_VERS_BASE,
  STATUT_DEPUIS_BASE,
  STATUTS,
  type Abonnement,
  type Canal,
  type Client,
  type Encaissement,
  type Forfait,
  type LigneRepartition,
  type MoyenEncaissement,
  type NatureLigne,
  type PaiePrimeLivreur,
  type StatutClient,
  type Course,
  type Cloture,
  type Creneau,
  type DonneesOps,
  type EcritureManuelle,
  type EvenementCloture,
  type LigneBalanceArchivee,
  type Facture,
  type LigneFacture,
  type MoyenReglement,
  type Livreur,
  type Membre,
  type Organisation,
  type RoleMembre,
  type StatutBase,
  type TarifsOperes,
  type TypeClient,
} from './types'
import { erreurCasquette } from './casquettes'

/* ====================================================================== */
/*  ACCÈS À LA CONSOLE                                                     */
/* ====================================================================== */

/**
 * Les rôles qui ont leur place ICI. Un compte livreur qui entrerait dans la
 * console échouerait sur toutes les écritures (RLS) sans comprendre pourquoi :
 * mieux vaut le refuser avec un message clair que le laisser tourner en rond.
 */
const ROLES_EQUIPE: RoleMembre[] = ['owner', 'dispatcher', 'accountant']

/** Ce que `resoudreOrganisation` peut répondre, avec le motif du refus. */
export type Acces =
  | { ok: true; orgId: string; role: RoleMembre }
  | { ok: false; motif: 'aucune_org' | 'compte_livreur' | 'org_cliente' | 'compte_suspendu' | 'invitation_en_attente' }

export const MOTIF_ACCES: Record<Exclude<Acces, { ok: true }>['motif'], string> = {
  aucune_org:
    "Ce compte n'est membre d'aucune équipe SwiftDeliver Opérations. Demandez une invitation au propriétaire de l'organisation, puis reconnectez-vous.",
  compte_livreur:
    "Ce compte n'a pas de rôle d'équipe (il est livreur) : utilisez l'app Livreur. Un même compte peut être livreur ET membre de l'équipe : demandez au propriétaire de vous donner un rôle.",
  org_cliente: "Ce compte appartient à une organisation cliente — votre espace est le portail SwiftDeliver.",
  compte_suspendu: "L'accès de ce compte a été suspendu par le propriétaire de l'organisation.",
  invitation_en_attente:
    "Votre invitation n'est pas encore active : connectez-vous avec l'e-mail ou le numéro WhatsApp auquel elle a été envoyée.",
}

export interface LigneMembership {
  org_id: string
  role: RoleMembre
  status: string | null
  organizations: { id: string; is_operator: boolean; name: string }
}

/**
 * Résout l'organisation OPÉRATRICE de l'utilisateur — après un mot de passe
 * comme après Google : c'est le MÊME contrôle, et le seul.
 *
 * ⚠️ Trois refus distincts. Cette console est la NÔTRE (modèle opérateur
 * complet, jamais bridé par un plan) ; le client SaaS a son portail.
 *
 *  • La version d'origine avait un repli `|| staff[0]` qui faisait entrer ici
 *    le propriétaire d'une organisation CLIENTE : on exige `is_operator`.
 *  • Un membre SUSPENDU entrait comme les autres : ni la RLS (`has_org_role`)
 *    ni les edge functions (`requireOrgRole`) ne regardent
 *    `memberships.status`. Le refus ci-dessous ne remplace pas la correction
 *    côté base (signalée), mais il évite au moins que la console fasse comme
 *    si de rien n'était.
 */
export async function resoudreOrganisation(profileId: string): Promise<Acces> {
  const { data, error } = await supabase
    .from('memberships')
    .select('org_id, role, status, organizations!inner(id,is_operator,name)')
    .eq('profile_id', profileId)

  if (error) throw error
  return choisirAccesOperateur((data ?? []) as unknown as LigneMembership[])
}

/** Ordre de préférence entre plusieurs adhésions d'équipe à des organisations opératrices. */
const RANG_ROLE: Record<RoleMembre, number> = { owner: 0, dispatcher: 1, accountant: 2, driver: 3 }

/**
 * La règle d'accès, PURE (testée sans réseau) — un compte, plusieurs casquettes (0045).
 *
 * Le même compte peut être livreur dans une organisation (adhésion `driver` + fiche) et membre de l'équipe dans une
 * autre : seules comptent ici les adhésions d'ÉQUIPE à une organisation OPÉRATRICE, et parmi elles les ACTIVES
 * (depuis 0023 la base refuse tout à une adhésion `invited` ou `suspended` : la version précédente laissait entrer
 * un membre seulement invité dans une console où tout échouait). Plusieurs candidates : choix DÉTERMINISTE
 * (rôle le plus large, puis nom), plus jamais « la première renvoyée ».
 */
export function choisirAccesOperateur(membres: LigneMembership[]): Acces {
  if (membres.length === 0) return { ok: false, motif: 'aucune_org' }

  const equipe = membres.filter((m) => ROLES_EQUIPE.includes(m.role))
  if (equipe.length === 0) return { ok: false, motif: 'compte_livreur' }

  const operatrices = equipe.filter((m) => m.organizations?.is_operator)
  if (operatrices.length === 0) return { ok: false, motif: 'org_cliente' }

  const actives = operatrices
    .filter((m) => (m.status ?? 'active') === 'active')
    .sort((a, b) => RANG_ROLE[a.role] - RANG_ROLE[b.role] || (a.organizations.name ?? '').localeCompare(b.organizations.name ?? '', 'fr'))
  const active = actives[0]
  if (!active) {
    return { ok: false, motif: operatrices.some((m) => m.status === 'invited') ? 'invitation_en_attente' : 'compte_suspendu' }
  }
  return { ok: true, orgId: active.org_id, role: active.role }
}

export async function seConnecter(email: string, motDePasse: string): Promise<string> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password: motDePasse })
  if (error) throw new Error(`Connexion refusée : ${error.message}`)
  if (!data.user) throw new Error('Connexion refusée.')
  return data.user.id
}

export async function seDeconnecter(): Promise<void> {
  await supabase.auth.signOut()
}

export async function sessionEnCours(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.user.id ?? null
}

/**
 * Active les invitations EN ATTENTE adressées à l'e-mail OU au numéro (confirmé par code WhatsApp) du compte connecté
 * (`accepter_invitations`, 0024 puis 0041). Une adhésion `invited` ne donne aucun droit : sans cet appel, un membre
 * invité par numéro se connecterait mais resterait « en attente ». À appeler à chaque connexion et restauration de
 * session, AVANT `resoudreOrganisation`. Fonction absente (migration non poussée) ou échec : 0, sans bloquer l'entrée.
 */
export async function accepterInvitations(): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('accepter_invitations')
    if (error) return 0
    const n = Number(data)
    return Number.isFinite(n) ? n : 0
  } catch {
    return 0
  }
}

/* ====================================================================== */
/*  CHARGEMENT                                                             */
/* ====================================================================== */

/* Formes brutes renvoyées par PostgREST, avant aplatissement. */
interface LigneClient {
  id: string
  type: TypeClient
  name: string
  contact_name: string | null
  phone: string | null
  email?: string | null
  quartier: string | null
  commune?: string | null
  notes?: string | null
  status?: StatutClient
  plan_code: string | null
  offslot_surcharge_fcfa: number | null
  default_pickup_address: string | null
  default_pickup_lat?: number | null
  default_pickup_lng?: number | null
  /** 0030 — absent tant que la migration n'est pas poussée. */
  option_longue_distance?: boolean | null
  /** Compte relié à la fiche (casquette « client », 0045) : sans lui, le client ne lit pas sa messagerie. */
  profile_id?: string | null
}
interface LigneCreneau extends Creneau {
  client_account_id: string
}
interface LigneAbonnement {
  id: string
  client_account_id: string
  plan_code: string
  status: Abonnement['status']
  prix_mensuel_fcfa: number | string
  quota_courses: number | string | null
  sur_devis: boolean | null
  option_longue_distance: boolean | null
  option_prix_mensuel_fcfa: number | string | null
  date_debut: string
  date_fin: string | null
  jour_facturation: number | string
}
interface LigneLivreur {
  id: string
  full_name: string
  phone: string | null
  status: Livreur['status']
  rating: number | string | null
  lat: number | null
  lng: number | null
  last_seen: string | null
  /** 0026 — absent tant que la migration n'est pas poussée. */
  position_source?: string | null
}
interface LigneCourse {
  id: string
  ref: string
  /** Course de l'app des particuliers (0047) : elle seule a un fil client ↔ livreur. */
  app_particulier?: boolean | null
  client_account_id: string
  status: StatutBase
  pickup_address: string | null
  dropoff_address: string | null
  dropoff_lat: number | null
  dropoff_lng: number | null
  pickup_lat: number | null
  pickup_lng: number | null
  client_phone?: string | null
  /** 0030 — absents tant que la migration n'est pas poussée. */
  requested_vehicle?: TypeVehicule | null
  distance_km?: number | string | null
  price_breakdown?: Course['price_breakdown']
  price_override_reason?: string | null
  /** 0028 — absents tant que la migration n'est pas poussée. */
  recipient_name?: string | null
  recipient_phone?: string | null
  price_fcfa: number
  surcharge_fcfa: number | null
  offslot: boolean | null
  driver_id: string | null
  channel: Canal | null
  created_at: string
  delivered_at: string | null
  proof_photo_path?: string | null
  proof_recipient_name?: string | null
  proof_lat?: number | null
  proof_lng?: number | null
  delivery_code?: string | null
}
interface LigneFactureBrute {
  id: string
  number: string
  client_account_id: string | null
  status: Facture['status'] | 'draft'
  issue_date: string
  subtotal_ht_fcfa: number
  vat_rate: number | string | null
  vat_fcfa: number
  total_ttc_fcfa: number
  notes: string | null
  /* 0023 — absents tant que la migration n'est pas poussée. */
  paid_at?: string | null
  payment_method?: string | null
  /* 0032 / 0033 — absents tant que les migrations ne sont pas poussées. */
  frais_mm_fcfa?: number | string | null
  periode_mois?: string | null
}

/**
 * Colonnes de `courses` lues par la console — chacune VÉRIFIÉE dans les
 * migrations (0001 adresses/coordonnées/horodatages, 0002 client/canal/
 * supplément, 0021 preuve). Un `select('*')` aurait marché aussi, mais une
 * liste explicite fait échouer la requête — bruyamment — si une colonne
 * venait à disparaître, au lieu de laisser passer des `undefined`.
 */
const COLONNES_COURSE_BASE =
  'id,ref,client_account_id,status,pickup_address,dropoff_address,pickup_lat,pickup_lng,dropoff_lat,dropoff_lng,client_phone,price_fcfa,surcharge_fcfa,offslot,driver_id,channel,created_at,delivered_at,proof_photo_path,proof_recipient_name,proof_lat,proof_lng,delivery_code,app_particulier'

/**
 * Destinataire de la course (0028). Tolérant : si la console est déployée AVANT la migration,
 * la première lecture échoue sur une colonne inconnue ; `chargerTout` retire alors ces deux
 * colonnes et recommence, au lieu de laisser la console vide. Une fois la migration poussée
 * (ordre normal : base d'abord), ce repli ne sert jamais.
 */
const COLONNES_DESTINATAIRE = ',recipient_name,recipient_phone'
let destinataireEnBase = true
/**
 * Grille tarifaire (0030) : véhicule demandé, distance, décomposition du prix, motif de dérogation.
 * Même tolérance que le destinataire : console déployée avant la migration → on relit sans ces colonnes.
 */
const COLONNES_GRILLE = ',requested_vehicle,distance_km,price_breakdown,price_override_reason'
const COLONNES_NOUVELLES = /requested_vehicle|distance_km|price_breakdown|price_override_reason/
let grilleEnBase = true
function colonnesCourse(): string {
  return COLONNES_COURSE_BASE + (destinataireEnBase ? COLONNES_DESTINATAIRE : '') + (grilleEnBase ? COLONNES_GRILLE : '')
}

const ORGANISATION_PAR_DEFAUT: Organisation = {
  name: 'Organisation',
  vat_registered: false,
  vat_rate: 18,
  currency: 'FCFA',
  invoice_prefix: 'FAC',
  require_delivery_proof: false,
}

/** Clés `org_documents` propres à cette console. */
export const CLE_ECRITURES = 'ecritures_manuelles'
export const CLE_PLAN = 'plan_comptable'

function versCourse(c: LigneCourse): Course {
  return {
    id: c.id,
    ref: c.ref,
    client_account_id: c.client_account_id,
    statut: STATUT_DEPUIS_BASE[c.status] ?? 'creee',
    pickup: c.pickup_address ?? '',
    dropoff: c.dropoff_address ?? '',
    price_fcfa: c.price_fcfa,
    surcharge_fcfa: c.surcharge_fcfa ?? 0,
    offslot: !!c.offslot,
    driver_id: c.driver_id,
    channel: c.channel ?? 'phone',
    created_at: c.created_at,
    delivered_at: c.delivered_at,
    dropoff_lat: c.dropoff_lat,
    dropoff_lng: c.dropoff_lng,
    pickup_lat: c.pickup_lat ?? null,
    pickup_lng: c.pickup_lng ?? null,
    requested_vehicle: c.requested_vehicle ?? null,
    distance_km: c.distance_km == null ? null : Number(c.distance_km),
    price_breakdown: c.price_breakdown ?? null,
    price_override_reason: c.price_override_reason ?? null,
    client_phone: c.client_phone ?? null,
    recipient_name: c.recipient_name ?? null,
    recipient_phone: c.recipient_phone ?? null,
    proof_photo_path: c.proof_photo_path ?? null,
    proof_recipient_name: c.proof_recipient_name ?? null,
    proof_lat: c.proof_lat ?? null,
    proof_lng: c.proof_lng ?? null,
    delivery_code: c.delivery_code ?? null,
    app_particulier: !!c.app_particulier,
  }
}

export function versLivreur(d: LigneLivreur): Livreur {
  return {
    id: d.id,
    full_name: d.full_name,
    phone: d.phone,
    status: d.status,
    rating: Number(d.rating ?? 5),
    lat: d.lat == null ? null : Number(d.lat),
    lng: d.lng == null ? null : Number(d.lng),
    last_seen: d.last_seen,
    position_source: d.position_source === 'boitier' ? 'boitier' : 'app',
  }
}

type PageReponse = PromiseLike<{ data: unknown[] | null; error: { message: string } | null }>

/**
 * Lit TOUTES les lignes d'une requête, par pages.
 *
 * ⚠️ PostgREST plafonne une réponse (1 000 lignes par défaut chez Supabase) :
 * au-delà, la suite est tronquée SANS erreur. Pour la comptabilité, une
 * troncature silencieuse est la pire des pannes — la balance reste équilibrée,
 * elle est seulement fausse. D'où la pagination, avec un plafond de sécurité
 * qui, lui, lève une erreur s'il est atteint.
 */
async function toutLire<T>(page: (de: number, a: number) => PageReponse, taille = 1000, max = 50): Promise<T[]> {
  const sortie: T[] = []
  for (let i = 0; i < max; i++) {
    const { data, error } = await page(i * taille, i * taille + taille - 1)
    if (error) throw new Error(error.message)
    const lot = (data ?? []) as T[]
    sortie.push(...lot)
    if (lot.length < taille) return sortie
  }
  throw new Error(`Plus de ${taille * max} lignes : chargement interrompu pour ne pas tronquer en silence.`)
}

/**
 * Lecture des factures. Les colonnes de règlement (`paid_at`, `payment_method`,
 * migration 0023) puis celles de la facturation mensuelle et des frais mobile money
 * (`periode_mois`, `frais_mm_fcfa`, migrations 0032 / 0033) peuvent ne pas exister
 * encore : on relit alors SANS elles plutôt que de casser toute la console, et
 * `reglements: false` le signale (les colonnes de règlement seules).
 */
async function lireFactures(orgId: string): Promise<{ lignes: LigneFactureBrute[]; reglements: boolean }> {
  const colonnes = 'id,number,client_account_id,status,issue_date,subtotal_ht_fcfa,vat_rate,vat_fcfa,total_ttc_fcfa,notes'
  const lire = (cols: string) =>
    toutLire<LigneFactureBrute>((de, a) =>
      supabase.from('invoices').select(cols).eq('org_id', orgId).order('created_at', { ascending: false }).order('id').range(de, a),
    )
  /* Du plus complet au plus ancien : on s'arrête au premier jeu de colonnes que la base connaît. */
  const jeux = [
    { cols: `${colonnes},paid_at,payment_method,frais_mm_fcfa,periode_mois`, reglements: true },
    { cols: `${colonnes},paid_at,payment_method`, reglements: true },
    { cols: colonnes, reglements: false },
  ]
  let derniere: unknown
  for (const j of jeux) {
    try {
      return { lignes: await lire(j.cols), reglements: j.reglements }
    } catch (e) {
      const message = (e as { message?: string } | null)?.message ?? ''
      if (!colonneReglementAbsente(e) && !/frais_mm_fcfa|periode_mois/.test(message)) throw e
      derniere = e
    }
  }
  throw derniere
}

/**
 * Lignes de toutes les factures (nature + HT) ET marquage des courses déjà facturées, en UNE lecture. La colonne
 * `kind` (0032) peut manquer : on relit alors sans elle, toutes les lignes sont des « courses à l'unité ».
 */
async function lireLignesFacturees(orgId: string): Promise<{ course_id: string | null; invoice_id: string; kind: NatureLigne; amount_ht_fcfa: number; invoices: { status: string } | null }[]> {
  interface Brute { course_id: string | null; invoice_id: string; kind?: NatureLigne; amount_ht_fcfa: number | string; invoices: { status: string } | null }
  const lire = (cols: string) =>
    toutLire<Brute>((de, a) =>
      supabase.from('invoice_lines').select(cols).eq('org_id', orgId).order('id').range(de, a),
    )
  let lignes: Brute[]
  try {
    lignes = await lire('invoice_id,course_id,kind,amount_ht_fcfa,invoices!inner(status)')
  } catch (e) {
    if (!/kind/.test((e as { message?: string } | null)?.message ?? '')) throw e
    lignes = await lire('invoice_id,course_id,amount_ht_fcfa,invoices!inner(status)')
  }
  return lignes.map((l) => ({ ...l, kind: l.kind ?? 'course', amount_ht_fcfa: Number(l.amount_ht_fcfa) }))
}

/** Forfaits PME (0031). Illisibles (migration non poussée, droit absent) = liste vide ET `ok: false` : on le DIT. */
async function lireForfaits(orgId: string): Promise<{ forfaits: Forfait[]; ok: boolean }> {
  try {
    const { data, error } = await supabase
      .from('forfaits_operes')
      .select('code,libelle,prix_mensuel_fcfa,quota_courses,actif,sort_order')
      .eq('org_id', orgId)
      .order('sort_order')
    if (error) return { forfaits: [], ok: false }
    return {
      ok: true,
      forfaits: ((data ?? []) as { code: string; libelle: string; prix_mensuel_fcfa: number | string | null; quota_courses: number | string | null; actif: boolean; sort_order: number }[]).map((f) => ({
        code: f.code,
        libelle: f.libelle,
        prix_mensuel_fcfa: f.prix_mensuel_fcfa == null ? null : Number(f.prix_mensuel_fcfa),
        quota_courses: f.quota_courses == null ? null : Number(f.quota_courses),
        actif: !!f.actif,
        sort_order: f.sort_order ?? 0,
      })),
    }
  } catch {
    return { forfaits: [], ok: false }
  }
}

/** Toutes les périodes d'abonnement des clients PME (0031), historique compris. */
async function lireAbonnements(orgId: string): Promise<{ abonnements: Abonnement[]; ok: boolean }> {
  try {
    const lignes = await toutLire<LigneAbonnement>((de, a) =>
      supabase
        .from('client_subscriptions')
        .select('id,client_account_id,plan_code,status,prix_mensuel_fcfa,quota_courses,sur_devis,option_longue_distance,option_prix_mensuel_fcfa,date_debut,date_fin,jour_facturation')
        .eq('org_id', orgId)
        .order('date_debut', { ascending: false })
        .order('id')
        .range(de, a),
    )
    return {
      ok: true,
      abonnements: lignes.map((l) => ({
        id: l.id,
        client_account_id: l.client_account_id,
        plan_code: l.plan_code,
        status: l.status,
        prix_mensuel_fcfa: Number(l.prix_mensuel_fcfa),
        quota_courses: l.quota_courses == null ? null : Number(l.quota_courses),
        sur_devis: !!l.sur_devis,
        option_longue_distance: !!l.option_longue_distance,
        option_prix_mensuel_fcfa: Number(l.option_prix_mensuel_fcfa ?? 0),
        date_debut: String(l.date_debut).slice(0, 10),
        date_fin: l.date_fin ? String(l.date_fin).slice(0, 10) : null,
        jour_facturation: Number(l.jour_facturation),
      })),
    }
  } catch {
    return { abonnements: [], ok: false }
  }
}

/** Paiements de courses enregistrés (0033). Illisibles = liste vide ET `ok: false`. */
async function lireEncaissements(orgId: string): Promise<{ encaissements: Encaissement[]; ok: boolean }> {
  try {
    const lignes = await toutLire<{ id: string; course_id: string; client_account_id: string | null; moyen: MoyenEncaissement; amount_fcfa: number | string; frais_fcfa: number | string | null; paid_at: string | null; created_at: string; status: Encaissement['status']; note: string | null }>((de, a) =>
      supabase
        .from('course_payments')
        .select('id,course_id,client_account_id,moyen,amount_fcfa,frais_fcfa,paid_at,created_at,status,note')
        .eq('org_id', orgId)
        .order('created_at')
        .order('id')
        .range(de, a),
    )
    return {
      ok: true,
      encaissements: lignes.map((l) => ({
        id: l.id,
        course_id: l.course_id,
        client_account_id: l.client_account_id,
        moyen: l.moyen,
        amount_fcfa: Number(l.amount_fcfa),
        frais_fcfa: Number(l.frais_fcfa ?? 0),
        paid_at: l.paid_at ?? l.created_at,
        status: l.status,
        note: l.note,
      })),
    }
  } catch {
    return { encaissements: [], ok: false }
  }
}

/** Primes payées aux livreurs (0044). Illisibles (dispatcher : RLS, ou migration absente) = liste vide. */
async function lirePaiesPrimes(orgId: string): Promise<PaiePrimeLivreur[]> {
  try {
    const { data, error } = await supabase
      .from('paies_primes_livreurs')
      .select('id,driver_nom,mois,montant_fcfa,moyen,reference,paye_le')
      .eq('org_id', orgId)
      .order('paye_le')
    if (error || !data) return []
    return (data as { id: string; driver_nom: string; mois: string; montant_fcfa: number | string; moyen: PaiePrimeLivreur['moyen']; reference: string | null; paye_le: string }[]).map((l) => ({
      ...l,
      mois: String(l.mois).slice(0, 10),
      montant_fcfa: Number(l.montant_fcfa),
    }))
  } catch {
    return []
  }
}

/** « Encaissé aujourd'hui » vu par la base (RPC `encaisse_du_jour`, 0033) ; `null` si elle est illisible. */
async function lireEncaisseJour(orgId: string): Promise<number | null> {
  try {
    const { data, error } = await supabase.rpc('encaisse_du_jour', { p_org: orgId })
    if (error || !data) return null
    const total = Number((data as { total?: unknown }).total)
    return Number.isFinite(total) ? total : null
  } catch {
    return null
  }
}

/** Lecture des clôtures (0008). Indisponibles = signalé, pas bloquant. */
async function chargerClotures(orgId: string): Promise<{ clotures: Cloture[]; evenements: EvenementCloture[]; ok: boolean }> {
  const [a, b] = await Promise.all([
    supabase
      .from('accounting_closures')
      .select('id,fiscal_year,lock_date,closed_at,resultat,produits,charges,nb_comptes,balance,checksum')
      .eq('org_id', orgId)
      .order('closed_at'),
    supabase.from('accounting_closure_events').select('id,fiscal_year,action,detail,at').eq('org_id', orgId).order('at'),
  ])
  if (a.error || b.error) return { clotures: [], evenements: [], ok: false }
  interface LC {
    id: string
    fiscal_year: number
    lock_date: string
    closed_at: string
    resultat: number | string
    produits: number | string
    charges: number | string
    nb_comptes: number
    balance: LigneBalanceArchivee[] | null
    checksum: string | null
  }
  interface LE {
    id: string
    fiscal_year: number
    action: 'cloture' | 'reouverture'
    detail: string | null
    at: string
  }
  return {
    ok: true,
    clotures: ((a.data ?? []) as LC[]).map((r) => ({
      id: r.id,
      annee: r.fiscal_year,
      dateVerrou: String(r.lock_date).slice(0, 10),
      clotureLe: r.closed_at,
      resultat: Number(r.resultat),
      produits: Number(r.produits),
      charges: Number(r.charges),
      nbComptes: r.nb_comptes,
      balance: Array.isArray(r.balance) ? r.balance : [],
      empreinte: r.checksum,
    })),
    evenements: ((b.data ?? []) as LE[]).map((r) => ({
      id: r.id,
      annee: r.fiscal_year,
      action: r.action,
      detail: r.detail ?? '',
      le: r.at,
    })),
  }
}

/** Lecture des registres `org_documents` de la console. */
async function chargerDocuments(
  orgId: string,
): Promise<{ ecritures: EcritureManuelle[]; plan: Record<string, string>; ok: boolean }> {
  const { data, error } = await supabase
    .from('org_documents')
    .select('key,value')
    .eq('org_id', orgId)
    .in('key', [CLE_ECRITURES, CLE_PLAN])
  if (error) return { ecritures: [], plan: {}, ok: false }
  const docs = new Map((data ?? []).map((r: { key: string; value: unknown }) => [r.key, r.value]))
  return { ecritures: lireEcrituresManuelles(docs.get(CLE_ECRITURES)), plan: lirePlan(docs.get(CLE_PLAN)), ok: true }
}

function lirePlan(v: unknown): Record<string, string> {
  const comptes = (v as { comptes?: unknown } | null)?.comptes
  if (!comptes || typeof comptes !== 'object') return {}
  const sortie: Record<string, string> = {}
  for (const [code, libelle] of Object.entries(comptes as Record<string, unknown>)) {
    if (/^[1-9]\d{1,7}$/.test(code) && typeof libelle === 'string' && libelle.trim()) sortie[code] = libelle.trim()
  }
  return sortie
}

/**
 * Charge TOUT l'état de la console en une passe.
 *
 * Le découpage en requêtes séparées par écran serait plus élégant, mais faux
 * ici : la comptabilité croise courses, factures, dépenses, écritures et
 * organisation. Deux chargements décalés donneraient une balance calculée sur
 * des instants différents.
 */
export async function chargerTout(orgId: string): Promise<DonneesOps> {
  /* Deux repli possibles (colonnes de la 0028 puis de la 0030 absentes) : au plus deux relectures. */
  for (let essai = 0; ; essai++) {
    try {
      return await chargerToutBrut(orgId)
    } catch (e) {
      const message = (e as { message?: string } | null)?.message ?? ''
      if (essai < 2 && destinataireEnBase && /recipient_/.test(message)) destinataireEnBase = false
      else if (essai < 2 && grilleEnBase && COLONNES_NOUVELLES.test(message)) grilleEnBase = false
      else throw e
    }
  }
}

async function chargerToutBrut(orgId: string): Promise<DonneesOps> {
  /* Fenêtre du dispatch : courses actives + tout ce qui a bougé depuis 24 h. */
  const depuis = new Date(Date.now() - 86_400_000).toISOString()

  const [cl, sl, dr, co, og, mb] = await Promise.all([
    supabase.from('client_accounts').select('*').eq('org_id', orgId).order('name'),
    supabase.from('client_time_slots').select('*').eq('org_id', orgId),
    supabase.from('drivers').select('id,full_name,phone,status,rating,lat,lng,last_seen').eq('org_id', orgId).order('full_name'),
    supabase
      .from('courses')
      .select(colonnesCourse())
      .eq('org_id', orgId)
      .not('client_account_id', 'is', null)
      .or(`status.in.(created,assigned,pickup,delivering),created_at.gte.${depuis}`)
      .order('created_at', { ascending: false })
      .limit(500),
    supabase
      .from('organizations')
      .select(
        'name,legal_form,nif,rccm,address,phone,email,vat_registered,vat_rate,currency,invoice_prefix,driver_join_code,brand_name,require_delivery_proof',
      )
      .eq('id', orgId)
      .single(),
    supabase
      .from('memberships')
      .select('id,role,status,permissions,title,profile_id,profiles!memberships_profile_id_fkey!inner(full_name,email,phone)')
      .eq('org_id', orgId),
  ])
  for (const r of [cl, sl, dr, co]) if (r.error) throw r.error

  /* Lectures paginées — comptabilité et historique. Les factures et leurs
     lignes sont réservées owner/comptable par la RLS : pour un dispatcher
     elles reviennent VIDES (pas en erreur), et l'écran Comptabilité lui est
     d'ailleurs fermé (voir `roles.ts`). */
  const [livreesBrutes, depenses, lecturesFactures, lignesFacturees, docs, clos] = await Promise.all([
    toutLire<LigneCourse>((de, a) =>
      supabase
        .from('courses')
        .select(colonnesCourse())
        .eq('org_id', orgId)
        .eq('status', 'delivered')
        .not('client_account_id', 'is', null)
        .order('delivered_at', { ascending: true })
        .order('id')
        .range(de, a),
    ),
    toutLire<DonneesOps['expenses'][number]>((de, a) =>
      supabase
        .from('expenses')
        .select('id,category,label,amount_fcfa,spent_on')
        .eq('org_id', orgId)
        .order('spent_on', { ascending: false })
        .order('id')
        .range(de, a),
    ),
    lireFactures(orgId),
    /* Lignes de factures : marquent les courses DÉJÀ facturées (sans ça, la facturation les
       reproposait pré-cochées et le client payait deux fois) et donnent la répartition du 7061. */
    lireLignesFacturees(orgId),
    chargerDocuments(orgId),
    chargerClotures(orgId),
  ])

  /* Messagerie à part : son indisponibilité (comptable : pas de droit de
     lecture) ne doit pas empêcher le reste de fonctionner. */
  /* Messagerie clients (0067) : liste des conversations, vide si la migration manque ou sans droit. */
  const conversationsClients = await lireConversationsClients(orgId)
  const dm = await supabase
    .from('driver_messages')
    .select('id,driver_id,sender,body,created_at,read_at')
    .eq('org_id', orgId)
    .order('created_at', { ascending: true })
    .limit(1000)

  /* Boîtiers GPS (0026) : lectures TOLÉRANTES — la console se redéploie avant que
     la migration soit poussée, et ne doit pas tomber pour autant. */
  const [lectureBoitiers, vehicules, sources, tarifs, lectureForfaits, lectureAbonnements, lectureEncaissements, encaisseJour, paiesPrimes] = await Promise.all([
    lireBoitiers(orgId),
    lireVehicules(orgId),
    lireSourcesPosition(orgId),
    lireTarifs(orgId),
    /* Forfaits, abonnements et encaissements (0031 / 0033) : même tolérance — la console se redéploie
       avant la migration sans tomber, et le DIT (`synchro`). */
    lireForfaits(orgId),
    lireAbonnements(orgId),
    lireEncaissements(orgId),
    lireEncaisseJour(orgId),
    lirePaiesPrimes(orgId),
  ])

  const creneauxParClient = new Map<string, Creneau[]>()
  for (const s of (sl.data ?? []) as LigneCreneau[]) {
    const liste = creneauxParClient.get(s.client_account_id) ?? []
    liste.push({ ...(s.id ? { id: s.id } : {}), weekday: s.weekday, start_time: String(s.start_time).slice(0, 5), end_time: String(s.end_time).slice(0, 5) })
    creneauxParClient.set(s.client_account_id, liste)
  }

  /* Tous les clients, y compris clos : une facture ou une course ancienne doit garder le nom et le
     type de son client dans les livres (`tousClients`, lu par la compta). Seuls les non clos sont
     proposés à la prise d'appel (`clients`). */
  const tousBruts = (cl.data ?? []) as LigneClient[]
  const nomClient = new Map(tousBruts.map((c) => [c.id, c.name]))
  const versClient = (c: LigneClient): Client => ({
    id: c.id,
    type: c.type,
    name: c.name,
    contact_name: c.contact_name,
    phone: c.phone,
    email: c.email ?? null,
    quartier: c.quartier,
    commune: c.commune ?? null,
    notes: c.notes ?? null,
    status: c.status ?? 'active',
    plan_code: c.plan_code,
    offslot_surcharge_fcfa: c.offslot_surcharge_fcfa ?? 0,
    default_pickup: c.default_pickup_address ?? c.quartier ?? '',
    default_pickup_note: '',
    default_pickup_lat: c.default_pickup_lat ?? null,
    default_pickup_lng: c.default_pickup_lng ?? null,
    option_longue_distance: !!c.option_longue_distance,
    slots: creneauxParClient.get(c.id) ?? [],
    a_un_compte: !!c.profile_id,
  })
  const tousClients: Client[] = tousBruts.map(versClient)
  const clients: Client[] = tousClients.filter((c) => c.status !== 'closed')

  const drivers: Livreur[] = ((dr.data ?? []) as LigneLivreur[])
    .map(versLivreur)
    .map((d) => (sources ? { ...d, position_source: sources.get(d.id) ?? 'app' } : d))
  const courses: Course[] = ((co.data ?? []) as unknown as LigneCourse[]).map(versCourse)
  const livrees: Course[] = livreesBrutes.map(versCourse)

  const brute = og.data as (Partial<Organisation> & { vat_rate?: number | string | null }) | null
  const org: Organisation = brute
    ? {
        ...ORGANISATION_PAR_DEFAUT,
        ...brute,
        name: brute.name ?? ORGANISATION_PAR_DEFAUT.name,
        vat_registered: !!brute.vat_registered,
        vat_rate: Number(brute.vat_rate ?? 18),
        currency: brute.currency ?? 'FCFA',
        invoice_prefix: brute.invoice_prefix ?? 'FAC',
        require_delivery_proof: !!brute.require_delivery_proof,
      }
    : ORGANISATION_PAR_DEFAUT

  interface LigneMembre {
    id: string
    role: RoleMembre
    status: string
    permissions: Record<string, boolean> | null
    title: string | null
    profiles: { full_name: string | null; email: string | null; phone?: string | null } | null
  }
  const members: Membre[] = ((mb.data ?? []) as unknown as LigneMembre[]).map((m) => ({
    id: m.id,
    /* Un compte à numéro seul n'a pas d'e-mail : à défaut de nom, on montre le numéro. */
    full_name: m.profiles?.full_name?.trim() || contactAffiche(m.profiles?.email, m.profiles?.phone) || '—',
    email: m.profiles?.email ?? '',
    phone: m.profiles?.phone ?? '',
    role: m.role,
    status: m.status,
    permissions: m.permissions ?? {},
    title: m.title,
  }))

  /* `draft` existe dans l'énumération mais la console n'en crée pas : un
     brouillon n'est pas une pièce comptable, il est écarté. */
  const facturesBrutes = lecturesFactures.lignes
  const invoices: Facture[] = facturesBrutes
    .filter((i): i is LigneFactureBrute & { status: Facture['status'] } => i.status !== 'draft')
    .map((i) => ({
      id: i.id,
      number: i.number,
      client_account_id: i.client_account_id,
      client_name: (i.client_account_id && nomClient.get(i.client_account_id)) || 'Client',
      status: i.status,
      issue_date: i.issue_date,
      subtotal_ht_fcfa: i.subtotal_ht_fcfa,
      vat_rate: Number(i.vat_rate ?? 0),
      vat_fcfa: i.vat_fcfa,
      total_ttc_fcfa: i.total_ttc_fcfa,
      notes: i.notes,
      /* Colonnes 0023 absentes (migration non poussée) : on garde le comportement
         d'avant — une facture « payée » l'est à sa date d'émission. */
      paid_at: lecturesFactures.reglements ? (i.paid_at ?? null) : i.status === 'paid' ? i.issue_date : null,
      payment_method: lecturesFactures.reglements && moyenValide(i.payment_method ?? '') ? (i.payment_method as MoyenReglement) : null,
      frais_mm_fcfa: Number(i.frais_mm_fcfa ?? 0),
      periode_mois: i.periode_mois ? String(i.periode_mois).slice(0, 10) : null,
      lines: [],
    }))

  const billed = new Set<string>()
  const lignesFactures: LigneRepartition[] = []
  for (const l of lignesFacturees) {
    /* Une facture ANNULÉE ne compte pas : sa course redevient facturable, et ses lignes ne sont plus du CA. */
    if (l.invoices && l.invoices.status === 'cancelled') continue
    if (l.course_id) billed.add(l.course_id)
    lignesFactures.push({ invoice_id: l.invoice_id, kind: l.kind, amount_ht_fcfa: l.amount_ht_fcfa })
  }

  return {
    clients,
    tousClients,
    drivers,
    courses,
    livrees,
    org,
    expenses: depenses.map((e) => ({ ...e, amount_fcfa: Number(e.amount_fcfa) })),
    members,
    invoices,
    billed,
    messages: (dm.data ?? []) as DonneesOps['messages'],
    conversationsClients,
    ecrituresManuelles: docs.ecritures,
    planComptable: docs.plan,
    clotures: clos.clotures,
    evenementsCloture: clos.evenements,
    tarifs,
    forfaits: lectureForfaits.forfaits,
    abonnements: lectureAbonnements.abonnements,
    encaissements: lectureEncaissements.encaissements,
    paiesPrimes,
    lignesFactures,
    encaisseJour,
    boitiers: lectureBoitiers.boitiers,
    vehicules,
    synchro: {
      documents: docs.ok,
      clotures: clos.ok,
      reglements: lecturesFactures.reglements,
      boitiers: lectureBoitiers.ok,
      abonnements: lectureForfaits.ok && lectureAbonnements.ok,
      encaissements: lectureEncaissements.ok,
    },
  }
}

/** Les lignes d'une facture — chargées à l'ouverture, pas avec la liste. */
export async function chargerLignesFacture(invoiceId: string): Promise<LigneFacture[]> {
  const lire = (cols: string) => supabase.from('invoice_lines').select(cols).eq('invoice_id', invoiceId).order('sort_order')
  let res = await lire('course_id,description,qty,unit_price_ht_fcfa,amount_ht_fcfa,kind')
  /* `kind` (0032) absent : console déployée avant la migration — on relit sans la colonne. */
  if (res.error && /kind/.test(res.error.message)) res = await lire('course_id,description,qty,unit_price_ht_fcfa,amount_ht_fcfa')
  if (res.error) throw res.error
  return ((res.data ?? []) as unknown as LigneFacture[]).map((l) => ({ ...l, qty: Number(l.qty) }))
}

/** Historique d'une course (`course_events`, 0001). */
export async function chargerEvenementsCourse(courseId: string): Promise<{ status: StatutBase; note: string | null; at: string }[]> {
  const { data, error } = await supabase
    .from('course_events')
    .select('status,note,at')
    .eq('course_id', courseId)
    .order('at')
  if (error) throw error
  return (data ?? []) as { status: StatutBase; note: string | null; at: string }[]
}

/**
 * URL signée de la photo de preuve (bucket PRIVÉ `preuves`, 0021). Valable
 * 5 minutes : assez pour l'afficher, trop peu pour servir de lien public.
 */
export async function urlPhotoPreuve(chemin: string): Promise<string> {
  const { data, error } = await supabase.storage.from('preuves').createSignedUrl(chemin, 300)
  if (error || !data?.signedUrl) throw new Error(error?.message ?? 'Photo indisponible')
  return data.signedUrl
}


/* ====================================================================== */
/*  TEMPS RÉEL                                                             */
/* ====================================================================== */

/** Toute modification de course dans l'org déclenche un rechargement. */
export function suivreCourses(orgId: string, surChangement: () => void): () => void {
  const canal = supabase
    .channel('ops-courses')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'courses', filter: `org_id=eq.${orgId}` },
      () => surChangement(),
    )
    .subscribe()
  return () => {
    void supabase.removeChannel(canal)
  }
}

/** Tout nouveau message de l'org (livreur ou dispatch) arrive en direct. */
export function suivreMessages(orgId: string, surMessage: (m: unknown) => void): () => void {
  const canal = supabase
    .channel('ops-messages')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'driver_messages', filter: `org_id=eq.${orgId}` },
      (charge) => surMessage(charge.new),
    )
    .subscribe()
  return () => {
    void supabase.removeChannel(canal)
  }
}


/**
 * Positions des livreurs en direct (`drivers`, publiée dans
 * `supabase_realtime` par 0001). On ne recharge PAS tout l'état à chaque
 * point GPS : on remplace la seule fiche concernée.
 */
export function suivreLivreurs(orgId: string, surLivreur: (l: Livreur) => void): () => void {
  const canal = supabase
    .channel('ops-livreurs')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'drivers', filter: `org_id=eq.${orgId}` },
      (charge) => {
        const n = charge.new as LigneLivreur | null
        if (n && n.id) surLivreur(versLivreur(n))
      },
    )
    .subscribe()
  return () => {
    void supabase.removeChannel(canal)
  }
}

/* ====================================================================== */
/*  ÉCRITURES                                                              */
/* ====================================================================== */

export async function creerClient(
  orgId: string,
  c: Omit<Client, 'id' | 'default_pickup_note' | 'email' | 'commune' | 'notes' | 'status' | 'a_un_compte'>,
): Promise<string> {
  const { data, error } = await supabase
    .from('client_accounts')
    .insert({
      org_id: orgId,
      type: c.type,
      name: c.name,
      contact_name: c.contact_name || null,
      phone: c.phone || null,
      quartier: c.quartier || null,
      plan_code: c.plan_code || null,
      offslot_surcharge_fcfa: c.offslot_surcharge_fcfa || 0,
      default_pickup_address: c.default_pickup || null,
      /* Collecte habituelle géolocalisée (épingle) — écrite SEULEMENT si posée ; sans elle la requête
         est celle d'avant. `option_longue_distance` (0030) de même : seulement quand on la coche. */
      ...(c.default_pickup_lat != null && c.default_pickup_lng != null
        ? { default_pickup_lat: c.default_pickup_lat, default_pickup_lng: c.default_pickup_lng }
        : {}),
      ...(c.option_longue_distance ? { option_longue_distance: true } : {}),
      /* Statut juridique (0035) : seulement s'il est choisi à la création ; sinon NULL, à qualifier à l'onboarding. */
      ...(c.statut_juridique ? { statut_juridique: c.statut_juridique } : {}),
    })
    .select()
    .single()
  if (error) throw error

  if (c.slots.length > 0) {
    const { error: e2 } = await supabase.from('client_time_slots').insert(
      c.slots.map((s) => ({
        client_account_id: data.id,
        org_id: orgId,
        weekday: s.weekday,
        start_time: s.start_time,
        end_time: s.end_time,
      })),
    )
    if (e2) throw e2
  }
  return data.id as string
}

export interface ParametresCourse {
  clientId: string
  client: Client | undefined
  pickup: string
  dropoff: string
  /** Points géolocalisés (épingle, Plus Code, adresse connue) — null tant qu'inconnus. */
  pickupLat?: number | null
  pickupLng?: number | null
  dropoffLat?: number | null
  dropoffLng?: number | null
  /** Véhicule demandé : le coefficient de la grille s'y applique. */
  vehicule?: TypeVehicule | null
  /**
   * Prix ANNONCÉ au client. Avec les deux points, la BASE le remplace par celui de la grille (verrou,
   * migration 0030) ; sans coordonnées, c'est lui qui est gardé (« saisie »).
   */
  total: number
  surcharge: number
  channel: Canal
  /** Destinataire distinct du client (0028) ; absent ou `null` = le client reçoit lui-même. */
  destinataire?: Destinataire | null
  /** Dérogation (propriétaire seulement : la base ignore le motif de tout autre rôle). */
  derogation?: { motif: string } | null
}

/** Ce que la base a RETENU : le prix du serveur fait foi, pas celui qu'on a envoyé. */
export interface CourseCreee {
  id: string
  ref: string | null
  prix: number
  supplement: number
  distanceKm: number | null
  source: 'grille' | 'override' | 'saisie' | null
  /** Majoration « forte demande » retenue par la base (0043), comprise dans `prix` ; 0 sans tension. */
  majoration: number
}

export async function creerCourse(orgId: string, p: ParametresCourse): Promise<CourseCreee> {
  const { data, error } = await supabase
    .from('courses')
    .insert({
      org_id: orgId,
      client_account_id: p.clientId,
      client_name: p.client?.name ?? '',
      client_phone: p.client?.phone ?? null,
      /* Colonnes du destinataire écrites SEULEMENT s'il y en a un : sans lui, la requête est celle
         d'avant la 0028. La base refuse un téléphone mal formé (courses_recipient_phone_chk). */
      ...(p.destinataire?.nom ? { recipient_name: p.destinataire.nom } : {}),
      ...(p.destinataire?.telephone ? { recipient_phone: p.destinataire.telephone } : {}),
      pickup_address: p.pickup,
      dropoff_address: p.dropoff,
      /* Coordonnées et véhicule (0030) : seulement quand on en a — sinon la requête reste celle d'avant. */
      ...(p.pickupLat != null && p.pickupLng != null ? { pickup_lat: p.pickupLat, pickup_lng: p.pickupLng } : {}),
      ...(p.dropoffLat != null && p.dropoffLng != null ? { dropoff_lat: p.dropoffLat, dropoff_lng: p.dropoffLng } : {}),
      ...(p.vehicule ? { requested_vehicle: p.vehicule } : {}),
      ...(p.derogation?.motif.trim() ? { price_override_reason: p.derogation.motif.trim() } : {}),
      price_fcfa: p.total,
      surcharge_fcfa: p.surcharge,
      offslot: p.surcharge > 0,
      channel: p.channel,
      status: 'created',
    })
    /* RETURNING rend la ligne APRÈS le déclencheur : c'est le prix de la base qu'on lit ici. */
    .select()
    .single()
  if (error) throw error

  await supabase.from('course_events').insert({
    course_id: data.id,
    org_id: orgId,
    status: 'created',
    note: "Prise d'appel console opérateur",
  })
  const source = (data.price_breakdown as { source?: string } | null)?.source
  return {
    id: data.id as string,
    ref: (data.ref as string | null) ?? null,
    prix: Number(data.price_fcfa),
    supplement: Number(data.surcharge_fcfa ?? 0),
    distanceKm: data.distance_km == null ? null : Number(data.distance_km),
    source: source === 'grille' || source === 'override' || source === 'saisie' ? source : null,
    majoration: Number(data.majoration_tension_fcfa ?? 0),
  }
}

/* ---------------------------------------------------------- adresses (0030) */

const COLONNES_ADRESSE = 'id,label,address_text,repere,lat,lng,source,use_count,last_used_at,contact_phone'

/** Adresses déjà utilisées par un client (collecte), les plus récentes d'abord. Illisible = liste vide : jamais bloquant. */
export async function adressesDuClient(orgId: string, clientId: string): Promise<AdresseConnue[]> {
  const { data, error } = await supabase
    .from('adresses')
    .select(COLONNES_ADRESSE)
    .eq('org_id', orgId)
    .eq('client_account_id', clientId)
    .order('last_used_at', { ascending: false })
    .limit(12)
  return error ? [] : ((data ?? []) as AdresseConnue[])
}

/** Adresses connues d'un numéro (livraison) — la clé est celle de la base (`cle_telephone`). */
export async function adressesDuTelephone(orgId: string, telephone: string): Promise<AdresseConnue[]> {
  const cle = cleTelephone(telephone)
  if (!cle) return []
  const { data, error } = await supabase
    .from('adresses')
    .select(COLONNES_ADRESSE)
    .eq('org_id', orgId)
    .eq('contact_phone_cle', cle)
    .order('last_used_at', { ascending: false })
    .limit(12)
  return error ? [] : ((data ?? []) as AdresseConnue[])
}

/** Retient un point (RPC `memoriser_adresse`). Ne fait JAMAIS échouer l'appelant : renvoie faux si ça n'a pas pu se faire. */
export async function memoriserAdresse(
  orgId: string,
  a: { clientId: string | null; telephone: string | null; label: string; texte: string; repere: string; lat: number; lng: number; source: 'epingle' | 'plus_code' },
): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('memoriser_adresse', {
      p_org: orgId,
      p_client: a.clientId,
      p_phone: a.telephone,
      p_label: a.label,
      p_address: a.texte || null,
      p_repere: a.repere || null,
      p_lat: a.lat,
      p_lng: a.lng,
      p_source: a.source,
    })
    return !error
  } catch {
    return false
  }
}

/* ------------------------------------------------- lien de position (0030) */

/** Crée la demande et rend le jeton EN CLAIR — il n'existe nulle part ailleurs : à utiliser tout de suite. */
export async function creerDemandePosition(courseId: string): Promise<string> {
  const { data, error } = await supabase.rpc('creer_demande_position', { p_course: courseId })
  if (error) {
    if (/course_terminee/.test(error.message)) throw new Error('Cette course est terminée : inutile de demander une position.')
    if (/forbidden/.test(error.message)) throw new Error("Vous n'avez pas le droit de demander cette position.")
    throw new Error(error.message)
  }
  if (typeof data !== 'string' || !/^[0-9a-f]{48}$/.test(data)) throw new Error('Réponse inattendue de la base (jeton absent).')
  return data
}

/* ------------------------------------------------- grille tarifaire (0030) */

const COLONNES_TARIFS =
  'tranches,coefficients,seuil_longue_distance_km,prix_km_sup_fcfa,prix_km_sup_option_fcfa,option_longue_distance_mensuel_fcfa,supplement_hors_creneau_fcfa,frais_mm_encaissement_wave,frais_mm_encaissement_om,frais_mm_decaissement_wave,frais_mm_decaissement_om,arrondi_fcfa,grille_provisoire,updated_at'
/**
 * Barème progressif et tension (0043). Même tolérance que les colonnes de courses : console déployée AVANT la
 * migration → la lecture avec ces colonnes échoue, on relit sans elles (valeurs par défaut, tension désactivée).
 */
const COLONNES_TARIFS_0043 =
  ',prix_minimum_fcfa,tension_active,tension_rayon_km,tension_seuil,tension_pente_pct,tension_plafond_pct,tension_part_livreur_pct'
let tarifs0043EnBase = true

function versTarifs(r: Record<string, unknown>): TarifsOperes {
  const n = (v: unknown, defaut: number) => (v == null || v === '' ? defaut : Number(v))
  const g = GRILLE_PAR_DEFAUT
  return {
    tranches: Array.isArray(r.tranches)
      ? (r.tranches as { jusqu_a_km: number | string; prix_fcfa: number | string }[]).map((t) => ({ jusqu_a_km: Number(t.jusqu_a_km), prix_fcfa: Number(t.prix_fcfa) }))
      : g.tranches,
    coefficients: { ...g.coefficients, ...Object.fromEntries(Object.entries((r.coefficients ?? {}) as Record<string, unknown>).map(([k, v]) => [k, Number(v)])) },
    seuil_longue_distance_km: n(r.seuil_longue_distance_km, g.seuil_longue_distance_km),
    prix_km_sup_fcfa: n(r.prix_km_sup_fcfa, g.prix_km_sup_fcfa),
    prix_km_sup_option_fcfa: n(r.prix_km_sup_option_fcfa, g.prix_km_sup_option_fcfa),
    option_longue_distance_mensuel_fcfa: n(r.option_longue_distance_mensuel_fcfa, OPTION_LONGUE_DISTANCE_MENSUEL_DEFAUT),
    supplement_hors_creneau_fcfa: n(r.supplement_hors_creneau_fcfa, g.supplement_hors_creneau_fcfa),
    frais_mm_encaissement_wave: n(r.frais_mm_encaissement_wave, 0),
    frais_mm_encaissement_om: n(r.frais_mm_encaissement_om, 0),
    frais_mm_decaissement_wave: n(r.frais_mm_decaissement_wave, 0),
    frais_mm_decaissement_om: n(r.frais_mm_decaissement_om, 0),
    arrondi_fcfa: n(r.arrondi_fcfa, g.arrondi_fcfa),
    prix_minimum_fcfa: n(r.prix_minimum_fcfa, g.prix_minimum_fcfa),
    tension_active: r.tension_active === true,
    tension_rayon_km: n(r.tension_rayon_km, g.tension_rayon_km),
    tension_seuil: n(r.tension_seuil, g.tension_seuil),
    tension_pente_pct: n(r.tension_pente_pct, g.tension_pente_pct),
    tension_plafond_pct: n(r.tension_plafond_pct, g.tension_plafond_pct),
    tension_part_livreur_pct: n(r.tension_part_livreur_pct, g.tension_part_livreur_pct),
    bareme_progressif_en_base: 'prix_minimum_fcfa' in r,
    grille_provisoire: r.grille_provisoire !== false,
    updated_at: typeof r.updated_at === 'string' ? r.updated_at : null,
  }
}

/** Lit la grille ; `null` si elle est illisible (migration non poussée, droit absent) — la console le dit, elle ne tombe pas. */
export async function lireTarifs(orgId: string): Promise<TarifsOperes | null> {
  try {
    let r = await supabase.from('tarifs_operes').select(COLONNES_TARIFS + (tarifs0043EnBase ? COLONNES_TARIFS_0043 : '')).eq('org_id', orgId).maybeSingle()
    if (r.error && tarifs0043EnBase && /prix_minimum_fcfa|tension_/.test(r.error.message)) {
      tarifs0043EnBase = false
      r = await supabase.from('tarifs_operes').select(COLONNES_TARIFS).eq('org_id', orgId).maybeSingle()
    }
    if (r.error || !r.data) return null
    return versTarifs(r.data as unknown as Record<string, unknown>)
  } catch {
    return null
  }
}

/** Tension au point d'enlèvement, comptée par la base (RPC `tension_operee`, 0043) — pour l'aperçu de la prise d'appel. */
export interface TensionLue {
  active: boolean
  au_forfait: boolean
  /** null quand la base n'a rien compté (tension désactivée, client au forfait). */
  comptes: CompteTension | null
}

/** `null` si la base ne sait pas répondre (migration 0043 absente, droit, réseau) : l'aperçu dit alors « non évaluée ». */
export async function lireTension(orgId: string, lat: number, lng: number, clientId: string | null): Promise<TensionLue | null> {
  try {
    const { data, error } = await supabase.rpc('tension_operee', { p_org: orgId, p_lat: lat, p_lng: lng, p_client: clientId })
    if (error || !data) return null
    const d = data as { active?: boolean; au_forfait?: boolean; demande?: number | null; offre?: number | null }
    return {
      active: d.active === true,
      au_forfait: d.au_forfait === true,
      comptes: typeof d.demande === 'number' && typeof d.offre === 'number' ? { demande: d.demande, offre: d.offre } : null,
    }
  } catch {
    return null
  }
}

/** Primes « forte demande » d'un mois, par livreur (RPC `primes_tension_livreurs`, 0043 — propriétaire et comptable). */
export interface PrimesTension {
  livreurs: { driver_id: string | null; nom: string; courses: number; prime_fcfa: number; majoration_fcfa: number }[]
  total_prime_fcfa: number
}

export async function lirePrimesTension(orgId: string, mois: string): Promise<PrimesTension | null> {
  try {
    const { data, error } = await supabase.rpc('primes_tension_livreurs', { p_org: orgId, p_mois: mois })
    if (error || !data) return null
    return data as PrimesTension
  } catch {
    return null
  }
}

/** Écrit la grille (propriétaire seulement : la base refuse tout autre rôle, et vérifie la forme). */
export async function enregistrerTarifs(orgId: string, t: Omit<TarifsOperes, 'grille_provisoire' | 'updated_at'>): Promise<void> {
  const { data, error } = await supabase
    .from('tarifs_operes')
    .update({
      tranches: t.tranches,
      coefficients: t.coefficients,
      seuil_longue_distance_km: t.seuil_longue_distance_km,
      prix_km_sup_fcfa: t.prix_km_sup_fcfa,
      prix_km_sup_option_fcfa: t.prix_km_sup_option_fcfa,
      option_longue_distance_mensuel_fcfa: t.option_longue_distance_mensuel_fcfa,
      supplement_hors_creneau_fcfa: t.supplement_hors_creneau_fcfa,
      frais_mm_encaissement_wave: t.frais_mm_encaissement_wave,
      frais_mm_encaissement_om: t.frais_mm_encaissement_om,
      frais_mm_decaissement_wave: t.frais_mm_decaissement_wave,
      frais_mm_decaissement_om: t.frais_mm_decaissement_om,
      arrondi_fcfa: t.arrondi_fcfa,
      /* 0043 : envoyés seulement quand la base les connaît (sinon la mise à jour échouerait sur une colonne inconnue). */
      ...(tarifs0043EnBase
        ? {
            prix_minimum_fcfa: t.prix_minimum_fcfa,
            tension_active: t.tension_active,
            tension_rayon_km: t.tension_rayon_km,
            tension_seuil: t.tension_seuil,
            tension_pente_pct: t.tension_pente_pct,
            tension_plafond_pct: t.tension_plafond_pct,
            tension_part_livreur_pct: t.tension_part_livreur_pct,
          }
        : {}),
    })
    .eq('org_id', orgId)
    .select('org_id')
  if (error) throw new Error(error.message)
  /* La RLS filtre au lieu d'échouer : 0 ligne modifiée = pas le droit (ou pas de grille). */
  if (!data || data.length === 0) throw new Error("La grille n'a pas été enregistrée : seul le propriétaire peut la modifier.")
}

/**
 * ⚠️ Le cycle de vie passe par les edge functions `assign-course` et
 * `set-course-status`, comme le portail SaaS et l'app livreur — jamais par un
 * UPDATE écrit ici. Ces fonctions contrôlent la transition, horodatent,
 * journalisent l'événement et recalculent le statut du livreur EN TENANT
 * COMPTE DE SES AUTRES COURSES. La version écrite à la main repassait le
 * livreur à « disponible » dès qu'une course se terminait, même s'il en avait
 * encore une en cours : le dispatch le croyait libre et lui en empilait une.
 */
export async function assignerCourse(orgId: string, courseId: string, livreurId: string): Promise<void> {
  await appelerFonction('assign-course', { org_id: orgId, course_id: courseId, driver_id: livreurId })
}

export async function avancerCourse(orgId: string, course: Course): Promise<void> {
  const suivant = STATUTS[course.statut].suivant
  if (!suivant) return
  await appelerFonction('set-course-status', {
    org_id: orgId,
    course_id: course.id,
    status: STATUT_VERS_BASE[suivant],
    note: 'Console opérateur',
  })
}

export async function annulerCourse(orgId: string, courseId: string): Promise<void> {
  await appelerFonction('set-course-status', {
    org_id: orgId,
    course_id: courseId,
    status: 'cancelled',
    note: 'Annulée au dispatch',
  })
}

/** Fiche livreur seule, sans compte : le livreur ne pourra pas se connecter. */
export async function ajouterLivreur(orgId: string, d: { full_name: string; phone: string | null }): Promise<void> {
  const { error } = await supabase
    .from('drivers')
    .insert({ org_id: orgId, full_name: d.full_name, phone: d.phone || null, status: 'available' })
  if (error) throw error
}

/** Accès à remettre au livreur : e-mail + mot de passe temporaire, ou — numéro seul — connexion par code WhatsApp. */
export type IdentifiantsLivreur = AccesCree

/** Onboarding COMPLET : compte Auth + profil + membership driver + fiche liée. */
export async function creerCompteLivreur(
  orgId: string,
  d: { first_name: string; last_name: string; contact: ContactValide },
): Promise<IdentifiantsLivreur> {
  /* L'edge function exige `first_name` ET `last_name` (missing_params sinon) :
     la console TypeScript envoyait un seul `full_name` depuis la bascule du
     04/10/2026 — aucun livreur ne pouvait être créé (vu en test terrain le 06/10). */
  return appelerFonction<IdentifiantsLivreur>('create-driver-account', corpsCompteLivreur(orgId, d))
}

/* ---------------------------------------------------------- messagerie */

export async function envoyerMessage(orgId: string, livreurId: string, texte: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  const { error } = await supabase.from('driver_messages').insert({
    org_id: orgId,
    driver_id: livreurId,
    sender: 'dispatch',
    sender_profile_id: user?.id ?? null,
    body: texte,
  })
  // 0045 : refus « casquettes » (son propre fil de livreur, fiche d'une autre organisation) → phrase lisible.
  if (error) throw erreurCasquette(error)
}

export async function marquerFilLu(orgId: string, livreurId: string): Promise<void> {
  const { error } = await supabase
    .from('driver_messages')
    .update({ read_at: new Date().toISOString() })
    .eq('org_id', orgId)
    .eq('driver_id', livreurId)
    .eq('sender', 'driver')
    .is('read_at', null)
  if (error) throw error
}

/* ----------------------------------------------------------- factures */

export async function creerFacture(
  orgId: string,
  f: Omit<Facture, 'id' | 'number' | 'status' | 'paid_at' | 'payment_method' | 'frais_mm_fcfa' | 'periode_mois'> & { lines: LigneFacture[] },
): Promise<{ id: string; number: string }> {
  /* Le numéro vient d'une séquence SERVEUR : le calculer ici ferait deux
     factures du même numéro dès que deux personnes facturent en même temps. */
  const { data: numero, error: nErr } = await supabase.rpc('next_invoice_number', { p_org: orgId })
  if (nErr) throw nErr

  const { data, error } = await supabase
    .from('invoices')
    .insert({
      org_id: orgId,
      client_account_id: f.client_account_id || null,
      number: numero,
      status: 'issued',
      issue_date: f.issue_date,
      subtotal_ht_fcfa: f.subtotal_ht_fcfa,
      vat_rate: f.vat_rate,
      vat_fcfa: f.vat_fcfa,
      total_ttc_fcfa: f.total_ttc_fcfa,
      notes: f.notes || null,
    })
    .select()
    .single()
  if (error) throw error

  if (f.lines.length > 0) {
    const { error: lErr } = await supabase.from('invoice_lines').insert(
      f.lines.map((l, i) => ({
        invoice_id: data.id,
        org_id: orgId,
        course_id: l.course_id || null,
        description: l.description,
        qty: l.qty || 1,
        unit_price_ht_fcfa: l.unit_price_ht_fcfa || l.amount_ht_fcfa,
        amount_ht_fcfa: l.amount_ht_fcfa,
        sort_order: i,
      })),
    )
    /* Si les lignes sont refusées (course facturée entre-temps par quelqu'un
       d'autre), on RETIRE l'en-tête : une facture émise sans ligne aurait un
       total sans justification et polluerait le grand-livre. Le numéro est
       consommé — un trou dans la séquence vaut mieux qu'une facture fantôme. */
    if (lErr) {
      await supabase.from('invoices').delete().eq('id', data.id)
      throw new Error(
        /23505|déjà facturée/.test(lErr.message ?? '')
          ? (lErr.message ?? 'Une des courses est déjà facturée.')
          : lErr.message,
      )
    }
  }
  return { id: data.id as string, number: numero as string }
}

/**
 * Enregistre le règlement d'une facture par la base (`regler_facture`, 0033) : rôle, date et moyen vérifiés
 * côté serveur, statut `paid`, et les frais Wave / Orange Money calculés AU TAUX D'ALORS — la console ne
 * dicte jamais un montant de frais.
 *
 * Le déclencheur de clôture (0023) refuse un règlement daté d'une période clôturée (`periode_cloturee`, avec
 * son `detail`), et laisse passer le règlement d'une facture émise dans un exercice clos si sa date est
 * ouverte. La fonction refuse une facture déjà réglée ou annulée : un double clic ou une saisie concurrente ne
 * réécrit pas un règlement déjà posé.
 */
export async function reglerFacture(factureId: string, reglement: { date: string; moyen: MoyenReglement }): Promise<{ fraisFcfa: number }> {
  const { data, error } = await supabase.rpc('regler_facture', { p_facture: factureId, p_date: reglement.date, p_moyen: reglement.moyen })
  if (error) throw new Error(/periode_cloturee/.test(error.message) ? traduireErreurReglement(error) : traduireErreurEncaissement(error))
  return { fraisFcfa: Number((data as { frais_fcfa?: unknown } | null)?.frais_fcfa ?? 0) }
}

/** Annule un règlement saisi par erreur : la facture redevient émise, non réglée. */
export async function annulerReglementFacture(factureId: string): Promise<void> {
  const { data, error } = await supabase
    .from('invoices')
    .update({ paid_at: null, payment_method: null, status: 'issued' })
    .eq('id', factureId)
    .in('status', ['issued', 'paid'])
    .not('paid_at', 'is', null)
    .select('id')
  if (error) throw new Error(traduireErreurReglement(error))
  if (!data || data.length === 0)
    throw new Error("Aucune facture modifiée : elle a changé entre-temps, ou votre rôle ne permet pas de l'annuler. Rechargez la page.")
}

/* ------------------------------------------- facturation mensuelle (0032) */

/**
 * Aperçu des factures d'un mois : ce que la génération produirait, SANS rien écrire. Même code que la génération
 * côté base — l'aperçu ne peut pas diverger. `mois` = premier jour du mois, `AAAA-MM-01`.
 */
export async function apercuFacturesMois(orgId: string, mois: string, forcer = false): Promise<RapportFacturation> {
  const { data, error } = await supabase.rpc('apercu_factures_mois', { p_org: orgId, p_mois: mois, p_forcer: forcer })
  if (error) throw new Error(traduireErreurFacturation(error))
  return lireRapport(data)
}

/** Génère UNE facture par client PME abonné, montants calculés par la base. Idempotent : relancer ne duplique rien. */
export async function genererFacturesMois(orgId: string, mois: string, forcer = false): Promise<RapportFacturation> {
  const { data, error } = await supabase.rpc('generer_factures_mois', { p_org: orgId, p_mois: mois, p_forcer: forcer })
  if (error) throw new Error(traduireErreurFacturation(error))
  return lireRapport(data)
}

/* ------------------------------------------------- encaissements (0033) */

/**
 * Enregistre un paiement de course (particulier, course livrée) : écriture comptable D 551 | 571 | 521 / C 411,
 * plus D 631 / C 551 pour les frais Wave / Orange Money. Le montant restant dû et les frais sont RECALCULÉS par la
 * base ; `date` vide = aujourd'hui (Bamako).
 */
export async function enregistrerEncaissement(
  courseId: string,
  e: { moyen: MoyenEncaissement; montant: number; date?: string; note?: string },
): Promise<{ id: string; fraisFcfa: number; resteDu: number }> {
  const { data, error } = await supabase.rpc('enregistrer_encaissement_course', {
    p_course: courseId,
    p_moyen: e.moyen,
    p_montant: e.montant,
    p_date: e.date || null,
    p_note: e.note?.trim() || null,
  })
  if (error) throw new Error(traduireErreurEncaissement(error))
  const r = (data ?? {}) as { id?: string; frais_fcfa?: unknown; reste_du?: unknown }
  return { id: String(r.id ?? ''), fraisFcfa: Number(r.frais_fcfa ?? 0), resteDu: Number(r.reste_du ?? 0) }
}

/** Annule un encaissement saisi par erreur (propriétaire, comptable) : il sort des livres, le motif est gardé. */
export async function annulerEncaissement(paiementId: string, motif: string): Promise<void> {
  const { error } = await supabase.rpc('annuler_encaissement_course', { p_paiement: paiementId, p_motif: motif.trim() || null })
  if (error) throw new Error(traduireErreurEncaissement(error))
}

/* ----------------------------------------------- fiche client (édition) */

export interface ModificationClient {
  name?: string
  contact_name?: string | null
  phone?: string
  email?: string | null
  quartier?: string | null
  commune?: string | null
  notes?: string | null
  offslot_surcharge_fcfa?: number
  option_longue_distance?: boolean
  default_pickup_address?: string | null
  default_pickup_lat?: number | null
  default_pickup_lng?: number | null
  /** Pause / clôture / réactivation : l'abonnement SUIT (déclencheur, 0031). */
  status?: StatutClient
}

/**
 * Modifie la fiche d'un client (propriétaire, dispatch — `client_accounts_manage`). Sous RLS, une mise à jour non
 * autorisée touche zéro ligne sans erreur : on le détecte, sinon l'écran dirait « enregistré ».
 */
export async function modifierClient(clientId: string, patch: ModificationClient): Promise<void> {
  const { data, error } = await supabase.from('client_accounts').update(patch).eq('id', clientId).select('id')
  if (error) throw new Error(error.message)
  if (!data || data.length === 0) throw new Error('Modification refusée : seuls le propriétaire et le dispatch modifient une fiche client.')
}

/**
 * Enregistre les créneaux de la fiche en n'écrivant que l'ÉCART (ajouts, modifications, suppressions) : jamais
 * « tout supprimer puis recréer », qui perdrait les créneaux si l'écriture s'arrêtait à mi-chemin.
 * Ordre : ajouter, modifier, puis supprimer — un échec laisse un surplus, pas un manque.
 */
export async function enregistrerCreneaux(orgId: string, clientId: string, avant: Creneau[], apres: Creneau[]): Promise<void> {
  const { ajouter, modifier, supprimer } = diffCreneaux(avant, apres)
  if (ajouter.length > 0) {
    const { error } = await supabase.from('client_time_slots').insert(
      ajouter.map((c) => ({ client_account_id: clientId, org_id: orgId, weekday: c.weekday, start_time: c.start_time, end_time: c.end_time })),
    )
    if (error) throw new Error(error.message)
  }
  for (const c of modifier) {
    const { data, error } = await supabase
      .from('client_time_slots')
      .update({ weekday: c.weekday, start_time: c.start_time, end_time: c.end_time })
      .eq('id', c.id)
      .select('id')
    if (error) throw new Error(error.message)
    if (!data || data.length === 0) throw new Error('Créneau non modifié : droit insuffisant ou créneau déjà supprimé. Rechargez la page.')
  }
  if (supprimer.length > 0) {
    const { error } = await supabase.from('client_time_slots').delete().in('id', supprimer)
    if (error) throw new Error(error.message)
  }
}

/** Toutes les adresses mémorisées d'un client (collecte), pour la fiche : la liste de la prise d'appel s'arrête à 12. */
export async function adressesCompletesDuClient(orgId: string, clientId: string): Promise<AdresseConnue[]> {
  const { data, error } = await supabase
    .from('adresses')
    .select(COLONNES_ADRESSE)
    .eq('org_id', orgId)
    .eq('client_account_id', clientId)
    .order('last_used_at', { ascending: false })
    .limit(200)
  if (error) throw new Error(error.message)
  return (data ?? []) as AdresseConnue[]
}

export async function supprimerAdresse(orgId: string, adresseId: string): Promise<void> {
  const { data, error } = await supabase.from('adresses').delete().eq('org_id', orgId).eq('id', adresseId).select('id')
  if (error) throw new Error(error.message)
  if (!data || data.length === 0) throw new Error('Adresse non supprimée : droit insuffisant ou déjà supprimée.')
}

/* ------------------------------------------------- abonnements (0031) */

/** Souscrit un client PME à un forfait : prix et quota de la grille FIGÉS (ou négociés, sur un forfait sur devis). */
export async function abonnerClient(
  clientId: string,
  plan: string,
  o: { prix?: number | null; quota?: number | null; debut?: string; jour?: number } = {},
): Promise<void> {
  const { error } = await supabase.rpc('abonner_client', {
    p_client: clientId,
    p_plan: plan,
    p_prix: o.prix ?? null,
    p_quota: o.quota ?? null,
    p_debut: o.debut || null,
    p_jour: o.jour ?? 1,
  })
  if (error) throw new Error(traduireErreurAbonnement(error))
}

/** Change le forfait d'un client : effectif le 1er du mois suivant (jamais immédiat), au prix de la grille du jour. */
export async function changerForfaitClient(
  clientId: string,
  plan: string,
  o: { prix?: number | null; quota?: number | null; effet?: string } = {},
): Promise<void> {
  const { error } = await supabase.rpc('changer_forfait_client', {
    p_client: clientId,
    p_plan: plan,
    p_prix: o.prix ?? null,
    p_quota: o.quota ?? null,
    p_effet: o.effet || null,
  })
  if (error) throw new Error(traduireErreurAbonnement(error))
}

/**
 * Modifie les forfaits de l'organisation (propriétaire seul). Les abonnements EXISTANTS ne bougent pas : leur prix et
 * leur quota sont figés. Seuls les champs éditables partent (le code est la clé).
 */
export async function enregistrerForfaits(
  orgId: string,
  forfaits: Pick<Forfait, 'code' | 'libelle' | 'prix_mensuel_fcfa' | 'quota_courses' | 'actif'>[],
): Promise<void> {
  for (const f of forfaits) {
    const { data, error } = await supabase
      .from('forfaits_operes')
      .update({ libelle: f.libelle, prix_mensuel_fcfa: f.prix_mensuel_fcfa, quota_courses: f.quota_courses, actif: f.actif })
      .eq('org_id', orgId)
      .eq('code', f.code)
      .select('code')
    if (error) throw new Error(error.message)
    if (!data || data.length === 0) throw new Error('Forfaits non enregistrés : seul le propriétaire peut les modifier.')
  }
}

/* ------------------------------------------------------------- équipe */

export async function inviterMembre(
  orgId: string,
  m: { contact: ContactValide; role: RoleMembre; permissions?: Record<string, boolean>; title?: string | null },
): Promise<ReponseInvitation> {
  /* E-mail et/ou numéro WhatsApp (au moins un). Numéro seul : aucun e-mail ne part, la personne se connecte avec
     son code WhatsApp et l'invitation s'active à la connexion (`accepter_invitations`). */
  const r = await appelerFonction<ReponseInvitation | null>('invite-member', corpsInvitation(orgId, m))
  return r ?? { email: m.contact.email, phone: m.contact.phone }
}

/** Phrase de confirmation après `inviterMembre`. */
export const confirmationInvitation = messageInvitation

export async function modifierMembre(
  orgId: string,
  membershipId: string,
  patch: { role?: RoleMembre; permissions?: Record<string, boolean>; title?: string | null; status?: string },
): Promise<void> {
  await appelerFonction('set-member-role', { org_id: orgId, membership_id: membershipId, ...patch })
}

export async function suspendreMembre(orgId: string, membershipId: string): Promise<void> {
  await appelerFonction('set-member-role', { org_id: orgId, membership_id: membershipId, status: 'suspended' })
}


/* ====================================================================== */
/*  COMPTABILITÉ — registres, dépenses, clôtures, organisation             */
/* ====================================================================== */

async function utilisateur(): Promise<string | null> {
  const { data } = await supabase.auth.getUser()
  return data.user?.id ?? null
}

/** Relit le document le plus RÉCENT en base, juste avant de le modifier. */
async function lireDocument(orgId: string, cle: string): Promise<unknown> {
  const { data, error } = await supabase.from('org_documents').select('value').eq('org_id', orgId).eq('key', cle).maybeSingle()
  if (error) throw new Error(`Lecture du registre impossible : ${error.message}`)
  return (data as { value: unknown } | null)?.value ?? null
}

async function ecrireDocument(orgId: string, cle: string, valeur: unknown): Promise<void> {
  const { error } = await supabase
    .from('org_documents')
    .upsert({ org_id: orgId, key: cle, value: valeur, updated_by: await utilisateur() }, { onConflict: 'org_id,key' })
  if (error) throw new Error(`Enregistrement refusé : ${error.message}`)
}

/**
 * Ajoute une écriture manuelle au registre `org_documents/ecritures_manuelles`.
 *
 * ⚠️ Lecture-modification-écriture : on RELIT le document en base juste avant
 * d'ajouter, pour ne pas écraser l'écriture qu'un collègue vient de passer
 * depuis un autre poste. Il reste une fenêtre de quelques millisecondes ; la
 * fermer exigerait une table dédiée ou une fonction serveur (signalé).
 * Contrairement au portail SaaS, AUCUN repli localStorage : une écriture
 * comptable qui n'existe que sur un poste est pire qu'une écriture refusée.
 */
export async function ajouterEcritureManuelle(
  orgId: string,
  e: Omit<EcritureManuelle, 'id' | 'piece' | 'cree_le' | 'cree_par'>,
): Promise<EcritureManuelle> {
  const id = crypto.randomUUID()
  const nouvelle: EcritureManuelle = {
    ...e,
    id,
    piece: pieceManuelle(id),
    cree_le: new Date().toISOString(),
    cree_par: await utilisateur(),
  }
  const existantes = lireEcrituresManuelles(await lireDocument(orgId, CLE_ECRITURES))
  await ecrireDocument(orgId, CLE_ECRITURES, { version: 1, ecritures: [...existantes, nouvelle] })
  return nouvelle
}

export async function supprimerEcritureManuelle(orgId: string, id: string): Promise<void> {
  const existantes = lireEcrituresManuelles(await lireDocument(orgId, CLE_ECRITURES))
  await ecrireDocument(orgId, CLE_ECRITURES, { version: 1, ecritures: existantes.filter((x) => x.id !== id) })
}

/** Renomme ou ajoute un compte (libellé propre à l'organisation). */
export async function enregistrerCompte(orgId: string, code: string, libelle: string): Promise<void> {
  const actuel = (await lireDocument(orgId, CLE_PLAN)) as { comptes?: Record<string, string> } | null
  const comptes = { ...(actuel?.comptes ?? {}), [code.trim()]: libelle.trim() }
  await ecrireDocument(orgId, CLE_PLAN, { version: 1, comptes })
}

/** Dépense : passe par l'edge function `record-expense` (rôle + palier contrôlés côté serveur). */
export async function enregistrerDepense(
  orgId: string,
  d: { category: string; label: string; amount_fcfa: number; spent_on: string },
): Promise<void> {
  await appelerFonction('record-expense', { org_id: orgId, ...d })
}

export async function supprimerDepense(orgId: string, id: string): Promise<void> {
  const { error } = await supabase.from('expenses').delete().eq('org_id', orgId).eq('id', id)
  if (error) throw new Error(error.message)
}

/** Archive une clôture puis inscrit l'événement — tables EN AJOUT SEUL (0008). */
export async function cloturerExercice(
  orgId: string,
  c: { annee: number; fin: string; resultat: number; produits: number; charges: number; balance: LigneBalanceArchivee[]; empreinte: string; detail: string },
): Promise<void> {
  const uid = await utilisateur()
  const r1 = await supabase.from('accounting_closures').insert({
    org_id: orgId,
    fiscal_year: c.annee,
    lock_date: c.fin,
    closed_by: uid,
    resultat: c.resultat,
    produits: c.produits,
    charges: c.charges,
    nb_comptes: c.balance.length,
    balance: c.balance,
    checksum: c.empreinte,
  })
  if (r1.error) throw new Error(`Archive refusée : ${r1.error.message}`)
  const r2 = await supabase
    .from('accounting_closure_events')
    .insert({ org_id: orgId, fiscal_year: c.annee, action: 'cloture', detail: c.detail, actor: uid })
  if (r2.error) throw new Error(`Archive créée, mais l'événement de clôture a été refusé : ${r2.error.message}`)
}

/** Une réouverture n'efface rien : elle AJOUTE un événement. */
export async function rouvrirExercice(orgId: string, annee: number, detail: string): Promise<void> {
  const { error } = await supabase
    .from('accounting_closure_events')
    .insert({ org_id: orgId, fiscal_year: annee, action: 'reouverture', detail, actor: await utilisateur() })
  if (error) throw new Error(error.message)
}

/**
 * Réglages de l'organisation que le propriétaire a le droit de changer
 * (policy `org_owner_maj`, 0016). Les colonnes de facturation et de droits
 * (`is_operator`, `plan_tier`, `is_white_label`, `driver_join_code`) sont
 * verrouillées par un déclencheur : elles ne figurent volontairement pas ici.
 */
export type ReglagesOrganisation = Partial<
  Pick<
    Organisation,
    'name' | 'legal_form' | 'nif' | 'rccm' | 'address' | 'phone' | 'email' | 'vat_registered' | 'vat_rate' | 'invoice_prefix' | 'require_delivery_proof'
  >
>

export async function modifierOrganisation(orgId: string, patch: ReglagesOrganisation): Promise<void> {
  const { data, error } = await supabase.from('organizations').update(patch).eq('id', orgId).select('id')
  if (error) throw new Error(error.message)
  /* Sous RLS, une mise à jour non autorisée ne lève PAS d'erreur : elle
     touche zéro ligne. Sans ce contrôle, l'écran affichait « enregistré ». */
  if (!data || data.length === 0) throw new Error("Modification refusée : seul le propriétaire de l'organisation peut changer ces réglages.")
}

/* ====================================================================== */
/*  CRÉNEAUX                                                               */
/* ====================================================================== */

/** ISO : 1 = lundi … 7 = dimanche. `getDay()` rend 0 pour dimanche. */
export function jourIso(d: Date): number {
  const n = d.getDay()
  return n === 0 ? 7 : n
}

/**
 * Le client est-il dans un de ses créneaux MAINTENANT ?
 *
 * Un client particulier, ou un client sans créneau déclaré, est toujours « dans
 * les clous » : lui facturer un supplément hors créneau n'aurait aucun sens.
 */
export function dansCreneau(client: Client | undefined, maintenant = new Date()): { dedans: boolean; creneau: Creneau | null } {
  if (!client || client.type !== 'pme' || client.slots.length === 0) return { dedans: true, creneau: null }
  const jour = jourIso(maintenant)
  const minutes = maintenant.getHours() * 60 + maintenant.getMinutes()
  const enMinutes = (t: string): number => {
    const [h, m] = t.split(':').map(Number)
    return (h ?? 0) * 60 + (m ?? 0)
  }
  const creneau = client.slots.find(
    (s) => s.weekday === jour && minutes >= enMinutes(s.start_time) && minutes < enMinutes(s.end_time),
  )
  return { dedans: !!creneau, creneau: creneau ?? null }
}

/* ====================================================================== */
/*  PROFIL CONNECTÉ                                                        */
/* ====================================================================== */

export interface MonProfil {
  nom: string
  /** Vide pour un compte à numéro seul. */
  email: string
  /** E.164, vide pour un compte à e-mail seul. */
  telephone: string
  role: RoleMembre
  /** Intitulé libre défini par l'admin (« Opératrice · Dispatch »), sinon null. */
  titre: string | null
}


/**
 * Qui est connecté, pour le pied de la barre latérale.
 *
 * ⚠️ La console HTML affichait « Awa Maïga · Opératrice · Dispatch » ÉCRIT EN
 * DUR dans le balisage, et rien ne le remplaçait jamais : tout opérateur
 * connecté voyait ce nom, y compris le propriétaire. Résidu de la maquette
 * qui a survécu au retrait du mode démo. On lit le vrai profil.
 */
export async function monProfil(orgId: string): Promise<MonProfil | null> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const { data, error } = await supabase
    .from('memberships')
    .select('role,title,profiles!memberships_profile_id_fkey!inner(full_name,email,phone)')
    .eq('org_id', orgId)
    .eq('profile_id', user.id)
    .maybeSingle()
  if (error) throw error
  if (!data) return null

  const ligne = data as unknown as {
    role: RoleMembre
    title: string | null
    profiles: { full_name: string | null; email: string | null; phone?: string | null }
  }
  /* Le numéro de la session Auth (sans « + ») sert de repli si le profil n'a pas encore été recopié. */
  const telephone = ligne.profiles.phone || (user.phone ? `+${user.phone.replace(/\D/g, '')}` : '')
  return {
    nom: ligne.profiles.full_name?.trim() || ligne.profiles.email || (telephone ? afficherTelephone(telephone) : '') || '—',
    email: ligne.profiles.email ?? user.email ?? '',
    telephone,
    role: ligne.role,
    titre: ligne.title,
  }
}

/** « Awa Maïga » → « AM ». Deux lettres au plus, comme `initials()`. */
export function initiales(nom: string): string {
  return nom
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((m) => m[0] ?? '')
    .join('')
    .toUpperCase()
}

export function sousTitreProfil(p: MonProfil): string {
  return p.titre ?? LIBELLE_ROLE[p.role]
}

/**
 * Crée un compte e-mail + mot de passe. N'écrit AUCUNE ligne (rôle, profil,
 * équipe) : les droits sont accordés ensuite par un administrateur.
 * `sessionOuverte` = faux quand la confirmation par e-mail est active.
 */
export async function creerCompte(
  nom: string,
  email: string,
  motDePasse: string,
): Promise<{ sessionOuverte: boolean }> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password: motDePasse,
    options: {
      data: { full_name: nom },
      emailRedirectTo: window.location.origin + window.location.pathname,
    },
  })
  if (error) throw new Error(traduireErreurInscription(error.message, error.code, error.status))
  if (adresseDejaInscrite(data.user)) throw new Error(MESSAGE_COMPTE_EXISTANT)
  return { sessionOuverte: !!data.session }
}
